// Parent auth: JWT bearer token stored in localStorage + React context.
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "ll_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
const storeToken = (t) => (t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY));
export const authHeaders = () => { const t = getToken(); return t ? { Authorization: `Bearer ${t}` } : {}; };

function errText(detail, fallback) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ") || fallback;
  if (detail?.msg) return detail.msg;
  return fallback;
}

async function post(path, body) {
  const r = await fetch(`${API}/api/auth/${path}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(errText(data.detail, "Something went wrong. Please try again."));
  return data;
}

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);   // null = logged out
  const [ready, setReady] = useState(false); // finished initial /me check

  useEffect(() => {
    const t = getToken();
    if (!t) { setReady(true); return; }
    fetch(`${API}/api/auth/me`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setUser(d.user))
      .catch(() => storeToken(null))
      .finally(() => setReady(true));
  }, []);

  const login = useCallback(async (email, password) => {
    const d = await post("login", { email, password });
    storeToken(d.token); setUser(d.user); return d.user;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const d = await post("register", { email, password, name });
    storeToken(d.token); setUser(d.user); return d.user;
  }, []);

  const logout = useCallback(() => { storeToken(null); setUser(null); }, []);

  return <AuthCtx.Provider value={{ user, ready, login, register, logout }}>{children}</AuthCtx.Provider>;
}
